<template>
    <div ref="listingRoot" class="md:py-5 md:overflow-x-hidden min-h-[720px] scroll-mt-20">
        <ProductListingStructuredData :products="renderedProducts" />
        <LayoutBreadcrumb v-if="breadcrumbs?.length > 0" :breadcrumb="breadcrumbs" class="min-h-[24px] mb-2 md:mb-3" />

        <!-- Mobile: wrapper is display:contents so headline and filter bar stick individually
             (relative to the whole listing) while the subcategory navigation between them scrolls away -->
        <div class="contents md:block md:relative bg-base-100 brand-page-color-active:bg-brand-page-bg">
            <div
                ref="headline"
                class="sticky top-0 z-20 -mx-2 px-2 pt-2 flex flex-wrap items-center gap-4 pb-1 bg-base-100 brand-page-color-active:bg-brand-page-bg md:static md:mx-0 md:px-0 md:pt-0 md:border-b md:mb-3 md:pb-5"
            >
                <h1 v-if="getTranslatedProperty(category, 'name')" class="text-xl font-semibold md:max-w-xl">
                    {{ (category?.translated?.customFields?.custom_category_name_for_listing ?? category?.customFields?.custom_category_name_for_listing) || getTranslatedProperty(category, 'name') }}
                </h1>
                <div v-if="getElements && getElements.length > 0" class="badge badge-secondary px-3">
                    {{ getTotal }}
                </div>
            </div>

            <!-- Subcategory navigation for mobile -->
            <LazyProductListingSubcategoryNavigation v-if="showSubcategoriesOnMobile" class="md:hidden" />

            <div
                ref="filter"
                class="sticky top-[calc(--spacing(3)_+_var(--text-xl)_*_var(--text-xl--line-height))] z-20 -mx-2 px-2 mb-4 md-3 bg-base-100 brand-page-color-active:bg-brand-page-bg md:static md:mx-0 md:px-0"
                :style="headlineHeight ? { top: `${headlineHeight}px` } : undefined"
            >
                <div class="border-b py-3 md:pb-0 md:border-none md:pt-0">
                    <div class="min-h-11 md:min-h-8 grid grid-cols-2 gap-2 md:block">
                        <ProductListingSorter
                            v-if="getSortingOrders?.length"
                            class="md:absolute md:top-0 md:right-0 md:w-auto"
                            :limit="getLimit"
                            :current-sorting="getCurrentSortingOrder"
                            :available-sortings="getSortingOrders"
                            @update:sorting="onSortChange"
                        />

                        <div class="hidden md:block">
                            <template v-if="mounted">
                                <LazyProductListingFilter
                                    v-if="showFilter"
                                    :category-filter="extensions?.categoryTree"
                                    @filter-change="onFilterChange"
                                />
                            </template>
                            <template v-else>
                                <MiscSkeleton size="medium" />
                            </template>
                        </div>
                        <button
                            type="button"
                            class="btn btn-outline border-border md:hidden brand-page-color-active:text-brand-page-color brand-page-color-active:border-brand-page-color"
                            aria-haspopup="true"
                            :aria-expanded="sidenav.isOpen('filter').value"
                            @click="sidenav.toggle('filter')"
                            @keydown.enter.prevent="sidenav.toggle('filter')"
                        >
                            <MiscIcon name="settings-2" class="brand-page-color-active:text-brand-page-color" />
                            {{ $t('product.listing.filterTitle') }}
                            <template v-if="filterCount > 0">
                                ({{ filterCount }})
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="w-full md:w-auto flex justify-start pb-6 pt-4"
            :class="{'hidden': previousPage < 1}"
        >
            <ProductListingInfiniteScroll
                class="h-full"
                :page="previousPage"
                :direction="'prev'"
                :loading="loading"
                @load-page="loadPage"
            />
        </div>

        <div ref="listingElement" class="relative" :class="props.gridClasses">
            <ProductListingPageWindow
                v-for="pageInfo in currentListings"
                :key="pageInfo.page"
                :products="pageInfo.products ?? []"
                :teaser="pageInfo.teaser"
                :page="pageInfo.page"
                :limit="pageInfo.limit"
                :loading="loadingPageNumber === pageInfo.page"
                :grid-classes="props.gridClasses"
                :kevel-ad="pageInfo.kevelAd"
                :kevel-ad-loading="kevelAdLoading"
                :kevel-ad-target="kevelAdEnabled ? setKevelAdTarget : undefined"
            />
        </div>

        <div
            class="w-full md:w-auto flex justify-start mb-2"
            :class="{'hidden': nextPage > lastPage}"
        >
            <ProductListingInfiniteScroll
                v-if="getCurrentPage < lastPage"
                class="h-full"
                :page="nextPage"
                :direction="'next'"
                :loading="loading"
                @load-page="loadPage"
            />
        </div>

        <ProductListingPagination
            v-if="getTotalPagesCount > 1"
            class="sr-only"
            :current-page="getCurrentPage"
            :limit="getLimit"
            :total-pages="getTotalPagesCount"
        />
    </div>

    <Teleport v-if="isMounted" defer to="#productListingFilter">
        <LazyProductListingFilter
            ref="filterComponent"
            @filter-change="onFilterChange"
        />
    </Teleport>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { Schemas } from '#shopware'
import { useCategoryListing } from '@shopware/composables'
import { useResizeObserver, useMediaQuery, useElementSize } from '@vueuse/core'
import { getTranslatedProperty } from '@shopware/helpers'
import { productListingIncludes } from '~/utils/includes/productListing'

type ApiFilterParams = {
    manufacturer?: string
    properties?: string
    'min-price'?: number
    'max-price'?: number
    'shipping-free'?: boolean
    rating?: number
    tdxcats?: string
}

interface ProductListingProps {
    data: Schemas['ProductListingResult'],
    total?: boolean,
    pagination?: boolean,
    sorting?: boolean,
    gridClasses?: string,
    extensions?: Schemas['CmsElementProductListing']['extensions'] | null,
    showSubcategoriesOnMobile?: boolean
}

const props = withDefaults(defineProps<ProductListingProps>(), {
    total: true,
    pagination: true,
    sorting: true,
    gridClasses: 'grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 pb-4',
    extensions: null,
    showSubcategoriesOnMobile: false
})

// Initialize listing context for useCategoryListing composable
const {
    getCurrentListing,
    getCurrentPage,
    getCurrentSortingOrder,
    getElements,
    getLimit,
    getSortingOrders,
    getTotalPagesCount,
    getTotal,
    search,
    loading,
    setInitialListing,
} = useCategoryListing();
setInitialListing(props.data)
const nuxtApp = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { category } = useCategory()
const { breadcrumbs } = useBreadcrumbs()
const sidenav = useSidenav()

// Filter component ref to access exposed properties
const filterComponent = ref<{ filterIsset: number } | null>(null)
const filterCount = computed(() => filterComponent.value?.filterIsset ?? 0)

const mounted = ref(true)

type CurrentListing = {
    page: number
    products: typeof getElements.value
    teaser: ReturnType<typeof getTeasersArray>
    limit: number
    kevelAd: ParsedKevelAd | null
}

const currentListings = ref<CurrentListing[]>(
    [
        {
            page: getCurrentPage.value,
            products: getElements.value,
            teaser: getTeasersArray(getCurrentListing.value?.extensions),
            limit: getLimit.value,
            kevelAd: null,
        }
    ]
)
const loadingPageNumber = ref<number | null>(null) // Tracked separately from currentListings (rather than a `loading` field on its entries)
const listingElement = ref<HTMLElement | null>(null) // Add ref for intersection observer
const listingRoot = ref<HTMLElement | null>(null)
const filter = ref(null)
// Mobile: the filter bar sticks directly below the sticky headline, whose height varies when long names wrap.
// Until measured (SSR, scroll restoration after reload) the filter's top-[calc(...)] class falls back to the
// single-line headline height, built from the headline's own tokens: pt-2 + pb-1 (--spacing(3)) + text-xl line height
const headline = ref<HTMLElement | null>(null)
const { height: headlineHeight } = useElementSize(headline, undefined, { box: 'border-box' })
const showFilter = ref(false)
const isMounted = ref(false)

// Kevel plp-sponsored ad (hard-coded placement, not from CMS; excluded on search pages)
const kevelAdTarget = ref<HTMLElement | null>(null)
const isSearchPage = computed(() => route.path.includes('/search'))
const { ad: kevelAdData, isLoading: kevelAdLoading, isEnabled: kevelAdEnabled } = useKevelPlacement('plp-sponsored', {
    target: kevelAdTarget,
    breadcrumb: category.value?.extensions?.breadcrumb,
    skip: isSearchPage.value,
})

// Function ref handed down to the ad slot in ProductListingPage (rendered on page 1 only)
function setKevelAdTarget(el: Element | ComponentPublicInstance | null) {
    kevelAdTarget.value = el instanceof HTMLElement ? el : null
}

/**
 * Every product actually rendered right now, in display order — the input for the ItemList
 * structured data. Derived from `currentListings` rather than a single page so it stays
 * truthful after infinite scroll has appended further pages to the same document.
 */
const renderedProducts = computed(() =>
    currentListings.value.flatMap(listing => listing.products ?? [])
)

const previousPage = computed(() => {
    return currentListings.value[0]!.page - 1
})

const nextPage = computed(() => {
    return currentListings.value.at(-1)!.page + 1
})

const lastPage = computed(() => {
    return getTotalPagesCount.value
})

// Use item list observer for view_item_list (fires once when scrolled into view for initial load)
useItemListObserver(
    listingElement,
    category.value?.id || 'listing',
    getTranslatedProperty(category.value, 'name') || 'Product Listing',
    getElements.value || [],
    getCurrentPage.value,
    getLimit.value
)

const isLargeScreen = useMediaQuery('(min-width: 768px)')

useResizeObserver(filter, () => {
    showFilter.value = isLargeScreen.value
})

// Inject Kevel ad into first page when available (only page 1 shows the ad).
// Its grid position is handled via CSS order in ProductListingPage.
watch(kevelAdData, (ad) => {
    if (currentListings.value[0] != null) {
        currentListings.value[0].kevelAd = ad
    }
}, { immediate: true })

/**
 * Perform search with URL update
 */
const performSearch = async (params: {
    page?: number
    limit?: number
    order?: string
    filters?: ApiFilterParams
}) => {
    // Start with current query params
    // If filters are being updated, explicitly unset old filter keys
    const baseQuery = params?.filters != null
        ? {
            ...route.query,
            manufacturer: undefined,
            properties: undefined,
            'min-price': undefined,
            'max-price': undefined,
            'shipping-free': undefined,
            rating: undefined,
            tdxcats: undefined,
        }
        : { ...route.query }

    // Create search params with correct types for API
    const searchParams = {
        ...baseQuery,
        p: params.page || getCurrentPage.value,
        limit: params.limit || getLimit.value,
        order: params.order?.toString() || getCurrentSortingOrder.value?.toString(),
        ...(params?.filters != null && { ...params?.filters })
    }

    // Convert to URL-compatible query (router only accepts strings/numbers, not booleans)
    const urlQuery = {
        ...searchParams,
        'shipping-free': searchParams['shipping-free'] ? 'true' : undefined
    }

    // Update URL
    await router.push({ query: urlQuery })

    await search({ ...searchParams, includes: productListingIncludes })
}

/**
 * Update listing with all parameters (resets to single page)
 */
const updateListing = async (params: {
    page?: number
    limit?: number
    order?: string
}) => {
    await performSearch(params)

    // Replace currentListings with new result (for filter/sort operations)
    currentListings.value = [
        {
            page: getCurrentPage.value,
            products: getElements.value,
            teaser: getTeasersArray(getCurrentListing.value?.extensions),
            limit: getLimit.value,
            kevelAd: kevelAdData.value,
        }
    ]
}

const onSortChange = async (order: string) => {
    // Reset to page 1 when changing the sorting
    await updateListing({ order, page: 1 })

    // Scrolled far down before sorting would otherwise leave the user looking at
    // whatever now-irrelevant content is at their old scroll position.
    await scrollListingIntoView()
}

const onFilterChange = async (filters: Schemas['ProductListingResult']['currentFilters']) => {
    // Transform CurrentFilters to API query format - only include values that are set
    const apiFilters: ApiFilterParams = {}

    if (filters.manufacturer?.length > 0) {
        apiFilters.manufacturer = filters.manufacturer.join('|')
    }
    if (filters.properties?.length > 0) {
        apiFilters.properties = filters.properties.join('|')
    }
    if (filters.price?.min) {
        apiFilters['min-price'] = Number(filters.price.min)
    }
    if (filters.price?.max) {
        apiFilters['max-price'] = Number(filters.price.max)
    }
    if (filters['shipping-free']) {
        apiFilters['shipping-free'] = true
    }
    if (filters.rating) {
        apiFilters.rating = Number(filters.rating)
    }
    if (filters.tdxcats) {
        // Handle both string and array formats
        apiFilters.tdxcats = Array.isArray(filters.tdxcats)
            ? filters.tdxcats.join('|')
            : filters.tdxcats
    }

    // Perform search with filters, reset to page 1
    await performSearch({
        filters: apiFilters,
        page: 1
    })

    // Replace currentListings (like sorting does)
    currentListings.value = [{
        page: getCurrentPage.value,
        products: getElements.value,
        teaser: getTeasersArray(getCurrentListing.value?.extensions),
        limit: getLimit.value,
        kevelAd: kevelAdData.value,
    }]

    // Scrolled far down before filtering would otherwise leave the user looking at
    // whatever now-irrelevant content is at their old scroll position.
    await scrollListingIntoView()
}

// Only mobile (< md) needs this: it's the only breakpoint where the filter/sorter bar
// is sticky, so it's the only one where applying a filter/sorter can leave it scrolled
// out of view above the viewport.
//
// Uses a manual getBoundingClientRect()-based scrollTo instead of scrollIntoView():
// scrollIntoView computes its target against the layout at call time, but Vue hasn't
// flushed the DOM update (currentListings shrinking back to one page) yet. Safari then
// animates toward a target based on the old, much taller layout and, once the DOM
// shrinks mid-scroll, clamps to the new max scroll position instead of retargeting --
// landing at the bottom of the now-short page. Awaiting nextTick() first ensures the
// DOM has already shrunk before the target offset is computed.
const scrollListingIntoView = async () => {
    if (isLargeScreen.value) return

    await nextTick()

    const element = listingRoot.value
    if (!element) return

    const rect = element.getBoundingClientRect()
    const scrollMarginTop = Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0

    window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - scrollMarginTop),
        behavior: 'smooth',
    })
}

// Extract teasers from extensions (a4gCurrentTeasers is keyed by ID, not an array)
function getTeasersArray(extensions?: Schemas['ProductListingResult']['extensions']): Schemas['A4gProductListingTeaser'][] {
    if (!extensions?.a4gCurrentTeasers) return []
    return Object.entries(extensions.a4gCurrentTeasers)
        .filter(([key]) => key !== 'apiAlias')
        .map(([_, value]) => value as Schemas['A4gProductListingTeaser'])
}

/**
 * Load a specific page for infinite scrolling
 */
const loadPage = async (page: number) => {
    // Capture current page before search to determine append/prepend
    const previousPageNumber = getCurrentPage.value
    const isNextPage = page > previousPageNumber

    if (isNextPage) {
        loadingPageNumber.value = page
        currentListings.value = currentListings.value.concat([{
            page,
            products: [],
            teaser: [],
            limit: getLimit.value,
            kevelAd: null,
        }])
    }

    // Perform search with URL update
    await performSearch({ page })

    // After search completes, apply the real page data
    const newListing = getCurrentListing.value
    if (!newListing) return

    const realPage = {
        page: newListing.page,
        products: getElements.value,
        teaser: getTeasersArray(newListing.extensions),
        limit: getLimit.value,
        kevelAd: null, // Kevel ad only shows on first page
    }

    if (page < previousPageNumber || page === previousPageNumber) {
        // Prepend for previous pages
        currentListings.value = [realPage, ...currentListings.value]
    }

    if (isNextPage) {
        currentListings.value = currentListings.value.map((pageInfo) => (
            pageInfo.page === page ? realPage : pageInfo
        ))
        loadingPageNumber.value = null
    }

    // Emit for analytics (infinite scroll pages)
    if (getElements.value && getElements.value.length > 0) {
        const productsWithAbsoluteIndex = addAbsoluteIndices(getElements.value, page, getLimit.value)
        nuxtApp.callHook('viewProductListing', {
            id: category.value?.id,
            name: getTranslatedProperty(category.value, 'name'),
            categoryPath: getCategoryPath(),
            products: productsWithAbsoluteIndex
        })
    }
}

// Helper function to generate category path from breadcrumbs
function getCategoryPath(): string {
    if (!breadcrumbs.value || breadcrumbs.value.length === 0) {
        return ''
    }

    return breadcrumbs.value
        .map(item => item.name)
        .join(' > ')
}

// Helper function to add absolute indices to products
function addAbsoluteIndices(products: Schemas['Product'][], page: number, limit: number) {
    const baseIndex = (page - 1) * limit
    return products.map((product, index) => ({
        ...product,
        absoluteIndex: baseIndex + index
    }))
}

onMounted(() => {
    mounted.value = true
    isMounted.value = true

    // Handle scroll restoration if we have navigation data
    const { getNavigationData, clearNavigationData, markScrollCompleted } = useProductNavigation()
    const navData = getNavigationData()
    
    if (navData && !navData.scrollCompleted) {
        let attempts = 0
        const maxAttempts = 10
        const minFramesBeforeScroll = 3

        const scrollToProduct = () => {
            const element = document.getElementById(`product-${navData.productId}`)

            if (!element) {
                attempts++
                if (attempts < maxAttempts) {
                    // Element not yet in DOM, try again on next frame
                    requestAnimationFrame(scrollToProduct)
                } else {
                    // Max attempts reached, give up and clear state
                    markScrollCompleted()
                    clearNavigationData()
                }
                return
            }

            // Element found, but wait for layout to stabilize
            attempts++
            if (attempts < minFramesBeforeScroll) {
                // Wait additional frames for layout to settle
                requestAnimationFrame(scrollToProduct)
                return
            }

            // Calculate position manually
            const rect = element.getBoundingClientRect()
            const elementTop = window.pageYOffset + rect.top
            const windowHeight = window.innerHeight
            const elementHeight = rect.height

            // Center the element in viewport
            const targetScrollTop = elementTop - (windowHeight / 2) + (elementHeight / 2)

            window.scrollTo({
                top: Math.max(0, targetScrollTop)
            })

            // This instant, script-driven jump can land lazy images in the viewport without
            // the browser ever firing a fresh IntersectionObserver entry for them -- ask
            // every lazy MiscImage to measure itself directly against the new scroll position.
            window.dispatchEvent(new Event('a4g:recheck-lazy-media'))

            // Mark as completed and clear store
            markScrollCompleted()
            clearNavigationData()
        }

        // Wait for Vue updates, then start checking
        nextTick(scrollToProduct)
    }
})
</script>
